import { withAITracking } from '@microsoft/applicationinsights-react-js';
import PersonAddAlt1 from '@mui/icons-material/PersonAddAlt1';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { OwnerAndUnitManagerDto, useProductCenterApi } from 'api';
import { unitStructureQuery } from 'api/queries';
import {
	unitConnectedEndCustomersQuery,
	unitKeys,
} from 'api/queries/UnitQueries';
import { AreaName } from 'domain/constants';
import {
	isConsumerRegisterButtonDisabled,
	isDemoDealerRegisterButtonDisabled,
	showEndConsumerSection,
} from 'domain/unit/Unit';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import { SectionTitleButtons } from 'library/components/controls/layout/SectionTitleButtons';
import { OperationType } from 'library/models/enums/OperationType';
import { selectUser } from 'library/redux/application/Selectors';
import { useAppSelector } from 'library/redux/hooks';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { reactPlugin } from 'main/azureInsights/config';
import {
	addEndCustomerPageWizardUrl,
	unitEditEndCustomerPageUrl,
	unitOverview_DemoDealerUsage_Url,
} from 'main/routes/urls/RouteUrls';
import { EditCustomerDialog } from 'modules/EndCustomer/Shared/EditCustomerDialog';
import { EndCustomerWithLanguageDto } from 'modules/EndCustomer/Shared/types';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { ConnectedCustomerCardDisplay } from 'modules/UnitOverview/Components/ConnectedCustomerCardDisplay';
import { LocalLinkButton } from 'modules/UnitOverview/Components/LocalLinkButton';

const EndCustomerSectionPage = (): React.JSX.Element => {
	const user = useAppSelector(selectUser);
	const { t } = useTranslation(['unitOverview']);
	const unitIds = useResolvedUnitIds();
	const [customerToEdit, setCustomerToEdit] =
		useState<EndCustomerWithLanguageDto | null>(null);

	const queryClient = useQueryClient();

	const { api } = useProductCenterApi();
	const {
		data: {
			owner,
			ownerChangedByInfo,
			unitManager,
			unitManagerChangedByInfo,
		},
	} = useSuspenseQuery<OwnerAndUnitManagerDto>(
		unitConnectedEndCustomersQuery(api, unitIds.id)
	);

	const { data: unitStructure } = useSuspenseQuery({
		...unitStructureQuery(unitIds.id),
	});

	const handleEditClose = async () => {
		await queryClient.invalidateQueries({
			queryKey: unitKeys.connectedCustomers(unitIds.id),
		});
		setCustomerToEdit(null);
	};

	const isRegistrationButtonDisabled =
		isConsumerRegisterButtonDisabled(unitStructure);

	const isConnected = showEndConsumerSection(unitStructure);
	const isCustomerRemoved = isConnected && !owner && !unitManager;

	return (
		<>
			{isConnected ? (
				<SectionTitleButtons>
					<AuthorizeComponent
						areaName={AreaName.Unit}
						permission={OperationType.Create}>
						<SectionButton
							text={t('unitOverview:end-customer-register-new')}
							icon={<PersonAddAlt1 />}
							isLink
							to={generatePath(unitEditEndCustomerPageUrl, {
								unitId: unitIds.publicId,
							})}
						/>
					</AuthorizeComponent>
				</SectionTitleButtons>
			) : (
				<AuthorizeComponent
					areaName={AreaName.Consumer}
					permission={OperationType.Create}>
					<LocalLinkButton
						data-testid="end-customer-registration-link"
						disabled={isRegistrationButtonDisabled}
						to={generatePath(addEndCustomerPageWizardUrl, {
							unitId: unitIds.publicId,
						})}>
						{t('unitOverview:end-customer-register-new')}
					</LocalLinkButton>
				</AuthorizeComponent>
			)}
			{!isDemoDealerRegisterButtonDisabled(unitStructure) && (
				<AuthorizeComponent
					areaName={AreaName.DemoUse}
					permission={OperationType.Create}>
					<LocalLinkButton
						data-testid="demo-registration-link"
						variant="outlined"
						to={generatePath(unitOverview_DemoDealerUsage_Url, {
							unitId: unitIds.publicId,
						})}
						sx={{ mt: { xs: 2, sm: 0 }, ml: { xs: 0, sm: 2 } }}>
						{t('unitOverview:register-demo-usage')}
					</LocalLinkButton>
				</AuthorizeComponent>
			)}
			{isCustomerRemoved ? (
				<Typography
					sx={{
						display: 'flex',
						gap: 1,
						alignItems: 'center',
					}}>
					<ReportProblemOutlined color="warning" />
					{t('unitOverview:end-customer-removed-due-to-gdpr')}
				</Typography>
			) : (
				isConnected && (
					<Box
						sx={{
							display: 'grid',

							gridTemplateColumns: {
								xs: '1fr',
								md: 'repeat(2, minmax(0, 1fr))',
							},

							gap: 2,
						}}>
						<ConnectedCustomerCardDisplay
							customer={owner}
							changedByInfo={ownerChangedByInfo}
							isExternal={user.isExternal}
							isOwner
							onEditCustomer={setCustomerToEdit}
							unitId={unitIds.id}
						/>
						<ConnectedCustomerCardDisplay
							customer={unitManager}
							changedByInfo={unitManagerChangedByInfo}
							isExternal={user.isExternal}
							isOwner={false}
							onEditCustomer={setCustomerToEdit}
							unitId={unitIds.id}
						/>
					</Box>
				)
			)}
			{customerToEdit != null && (
				<EditCustomerDialog
					customer={customerToEdit}
					onClose={handleEditClose}
					source="UnitOverview"
				/>
			)}
		</>
	);
};

export default withAITracking(
	reactPlugin,
	EndCustomerSectionPage,
	'UnitOverviewEndCustomerSectionPage',
	'application-insight-container'
);
